import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { SortIcon } from '@/components/common/SortIcon';
import { DeleteDialog } from '@/components/common/Dialogs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getLocalizedName } from '@/lib/utils';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { usersQueryKeys } from '../api';
import { UserStatusBadge } from './UserStatusBadge';
import { RunningTimer } from './RunningTimer';
import { CreateUserDialog } from './CreateUserDialog';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import type { User, UserFilters } from '../types';

interface GetColumnsParams {
  orderBy: string;
  order: 'asc' | 'desc' | '';
  onSort: (field: string) => void;
  filters: UserFilters;
}

export function useUserColumns({
  orderBy,
  order,
  onSort,
}: GetColumnsParams): ColumnDef<User>[] {
  const { t, i18n } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const currentUser = useAuthStore((state) => state.user);
  // STAFF-type users may not view other people's tasks; only their own row.
  const canViewOthersTasks = currentUser?.role?.type !== 'STAFF';
  // Viewing another user's KPI follows the KPI page rule: needs `kpi:get:all`.
  const canViewOthersKpi = hasAccess('kpi', 'get:all');

  const columns: ColumnDef<User>[] = [
    {
      accessorKey: 'name',
      header: () => (
        <div
          className="flex items-center cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => onSort('name')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('name')}
        >
          <p>{t('name')}</p>
          <SortIcon active={orderBy === 'name'} direction={order} />
        </div>
      ),
      cell: ({ row }) => (
        <Link
          to={`/app/users/${row.original.id}`}
          className="text-blue-700 hover:underline"
        >
          {row.original.name}
        </Link>
      ),
    },
    {
      accessorKey: 'phone',
      header: () => <p>{t('phone')}</p>,
      cell: ({ row }) => <div>{row.original.phone || '-'}</div>,
    },
    {
      accessorKey: 'role',
      header: () => <p>{t('role')}</p>,
      cell: ({ row }) => <div>{getLocalizedName(row.original?.role, i18n.language)}</div>,
    },
    {
      accessorKey: 'email',
      header: () => <p>{t('email')}</p>,
      cell: ({ row }) => <div>{row.original.email || '-'}</div>,
    },
    {
      accessorKey: 'status',
      header: () => <p>{t('status.status')}</p>,
      cell: ({ row }) => <UserStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'createdActivities',
      header: () => <p>{t('createdActivities')}</p>,
      cell: ({ row }) => {
        const activity = row?.original?.createdActivities?.[0] || null;
        return <RunningTimer activity={activity} />;
      },
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <div
          className="flex items-center cursor-pointer select-none"
          role="button"
          tabIndex={0}
          onClick={() => onSort('createdAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort('createdAt')}
        >
          <p>{t('createdAt')}</p>
          <SortIcon active={orderBy === 'createdAt'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const dateStr = row?.original?.createdAt;
        return dateStr ? (
          <div>{format(new Date(dateStr), 'yyyy-MM-dd HH:mm')}</div>
        ) : (
          <span>-</span>
        );
      },
    },
  ];

  // Add actions column if user has permissions
  if (hasAccess('user', 'update') || hasAccess('user', 'delete')) {
    columns.splice(0, 0, {
      id: 'actions',
      header: t('actionsLabel'),
      cell: ({ row }) => {
        const data = row.original;

        return (
          <div className="flex">
            {hasAccess('user', 'update') && <CreateUserDialog id={data.id} />}
            <DropdownMenu>
              <div className="flex items-center">
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={t('openActions')}
                    className="rounded-l-none hover:bg-muted border"
                  >
                    <ChevronDown size={20} />
                  </Button>
                </DropdownMenuTrigger>
              </div>
              <DropdownMenuContent className="min-w-[150px] flex flex-col gap-3 p-2">
                {hasAccess('user', 'change-password') && (
                  <ChangePasswordDialog id={data.id} />
                )}
                {hasAccess('user', 'delete') && (
                  <DeleteDialog
                    id={data.id}
                    title={data.name}
                    url="users"
                    queryKey={usersQueryKeys.lists()}
                  />
                )}
                {(canViewOthersTasks || data.id === currentUser?.id) && (
                  <Link
                    to={`/app/new-tasks?assignedStaffId=${data.id}`}
                    className="text-blue-700 hover:underline"
                  >
                    <Button
                      type="button"
                      variant="outline"
                      aria-label={t('viewTasks')}
                      className="hover:bg-muted w-full"
                    >
                      {t('tasks')}
                    </Button>
                  </Link>
                )}
                {(canViewOthersKpi || data.id === currentUser?.id) && (
                  <Link
                    to={`/app/kpi?userId=${data.id}`}
                    className="text-blue-700 hover:underline"
                  >
                    <Button
                      type="button"
                      variant="outline"
                      aria-label={t('viewKpi')}
                      className="hover:bg-muted w-full"
                    >
                      {t('KPI.kpi')}
                    </Button>
                  </Link>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      enableHiding: false,
    });
  }

  return columns;
}
